// src/pages/Dashboard/Admin/Requests/components/RequestsTable.jsx

import React from "react";
import { FaBug, FaEdit, FaEye, FaTrash } from "react-icons/fa";

import {
  formatDateTimeFR,
  getPriorityMeta,
  getRequestCreatedAt,
  getStatusMeta,
  truncateText,
} from "../utils/requestsDashboardUtils";

function SkeletonRows() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-4 sm:p-5 lg:p-6">
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="h-24 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FaBug />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucune requête trouvée
        </h3>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Ajoutez une demande ou modifiez les filtres.
        </p>
      </div>
    </section>
  );
}

function ActionButton({ title, onClick, disabled, children, tone = "base" }) {
  const tones = {
    base: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    info: "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300",
    primary:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    danger:
      "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-50 ${
        tones[tone] || tones.base
      }`}
    >
      {children}
    </button>
  );
}

export default function RequestsTable({
  requests,
  loading,
  deletingId,
  onEdit,
  onDelete,
  onOpenDetails,
}) {
  if (loading) return <SkeletonRows />;
  if (!requests.length) return <EmptyState />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Requêtes
          </p>

          <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            Liste des demandes
          </h2>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Les lignes urgentes sont mises en évidence. Ouvrez le détail pour
            lire la description complète.
          </p>
        </div>

        <div className="overflow-x-auto rounded-[22px] border border-slate-200 dark:border-slate-800">
          <table className="w-full min-w-[1080px] table-fixed">
            <colgroup>
              <col className="w-[120px]" />
              <col className="w-[155px]" />
              <col className="w-[280px]" />
              <col className="w-[360px]" />
              <col className="w-[140px]" />
              <col className="w-[150px]" />
            </colgroup>

            <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
              <tr>
                <th className="sticky left-0 z-30 border-r border-slate-200 bg-slate-50 px-3 py-3 text-left font-bold dark:border-slate-700 dark:bg-slate-950">
                  Priorité
                </th>
                <th className="px-3 py-3 text-left font-bold">Statut</th>
                <th className="px-3 py-3 text-left font-bold">Titre</th>
                <th className="px-3 py-3 text-left font-bold">Description</th>
                <th className="px-3 py-3 text-left font-bold">Créée le</th>
                <th className="px-3 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-white/60 text-sm dark:divide-slate-800">
              {requests.map((request) => {
                const priority = getPriorityMeta(request.priority);
                const status = getStatusMeta(request.status);
                const preview = truncateText(request.description, 220);

                return (
                  <tr key={request.id} className={`transition ${priority.row}`}>
                    <td
                      className={`sticky left-0 z-20 border-r border-slate-200 px-3 py-4 align-top dark:border-slate-700 ${priority.row}`}
                    >
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${priority.badge}`}
                      >
                        {priority.label}
                      </span>
                    </td>

                    <td className="px-3 py-4 align-top">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${status.badge}`}
                      >
                        {status.shortLabel}
                      </span>
                    </td>

                    <td className="px-3 py-4 align-top">
                      <p className="line-clamp-2 font-bold text-slate-900 dark:text-white">
                        {request.title || "Sans titre"}
                      </p>
                    </td>

                    <td className="px-3 py-4 align-top">
                      {preview ? (
                        <p
                          className="line-clamp-3 text-xs leading-5 text-slate-600 dark:text-slate-300"
                          title={preview}
                        >
                          {preview}
                        </p>
                      ) : (
                        <p className="text-xs text-slate-400 dark:text-slate-500">
                          Aucune description
                        </p>
                      )}

                      <button
                        type="button"
                        onClick={() => onOpenDetails(request)}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-indigo-700 underline underline-offset-2 hover:opacity-80 dark:text-indigo-300"
                      >
                        <FaEye className="text-[11px]" />
                        Voir le détail
                      </button>
                    </td>

                    <td className="px-3 py-4 align-top text-xs font-medium text-slate-600 dark:text-slate-300">
                      {formatDateTimeFR(getRequestCreatedAt(request))}
                    </td>

                    <td className="px-3 py-4 align-top">
                      <div className="flex items-center justify-end gap-1.5">
                        <ActionButton
                          title="Voir le détail"
                          onClick={() => onOpenDetails(request)}
                          tone="info"
                        >
                          <FaEye />
                        </ActionButton>

                        <ActionButton
                          title="Modifier"
                          onClick={() => onEdit(request)}
                          tone="primary"
                        >
                          <FaEdit />
                        </ActionButton>

                        <ActionButton
                          title="Supprimer"
                          onClick={() => onDelete(request)}
                          disabled={deletingId === request.id}
                          tone="danger"
                        >
                          <FaTrash />
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}